// SPDX-License-Identifier: AGPL-3.0-or-later

export const OFFSCREEN_RELEASE_DELAY_MS = 30_000;

const trackedPlayers = new Map<HTMLMediaElement, () => void>();
// Every entry waits the same delay, so insertion order is deadline order and only the first one needs a timer.
const pendingReleases = new Map<HTMLMediaElement, {releaseAt: number; release: () => void}>();
let releaseTimer: ReturnType<typeof setTimeout> | null = null;

function pauseOtherPlayers(started: HTMLMediaElement): void {
	for (const [media, pause] of trackedPlayers) {
		if (media === started || media.paused) continue;
		if (document.pictureInPictureElement === media) continue;
		pause();
	}
}

export function trackVideoPlayback(media: HTMLMediaElement, pause: () => void): () => void {
	const handlePlay = () => pauseOtherPlayers(media);
	trackedPlayers.set(media, pause);
	media.addEventListener('play', handlePlay);
	return () => {
		media.removeEventListener('play', handlePlay);
		trackedPlayers.delete(media);
		cancelOffscreenRelease(media);
	};
}

function armReleaseTimer(): void {
	if (releaseTimer !== null) {
		clearTimeout(releaseTimer);
		releaseTimer = null;
	}
	const next = pendingReleases.values().next();
	if (next.done) return;
	releaseTimer = setTimeout(flushDueReleases, Math.max(0, next.value.releaseAt - Date.now()));
}

function flushDueReleases(): void {
	releaseTimer = null;
	const now = Date.now();
	for (const [media, pending] of pendingReleases) {
		if (pending.releaseAt > now) break;
		pendingReleases.delete(media);
		pending.release();
	}
	armReleaseTimer();
}

export function scheduleOffscreenRelease(media: HTMLMediaElement, release: () => void): void {
	pendingReleases.delete(media);
	pendingReleases.set(media, {releaseAt: Date.now() + OFFSCREEN_RELEASE_DELAY_MS, release});
	armReleaseTimer();
}

export function cancelOffscreenRelease(media: HTMLMediaElement): void {
	if (pendingReleases.delete(media)) armReleaseTimer();
}
